import {
  identifier,
  type Identifier,
  QualifiedMap,
  identifierToString,
} from '@packages/systems/wfdl-Qualified';

import * as Variable from '../Variable/Variable';

// Opaque type removed - see /packages  /utilities/type-safety/README.md for more information
export type Map<A> = Readonly<{
  local: Record<Identifier, A>;
  plugins: QualifiedMap<Identifier, A>;
}>;

const _insert = Variable.match({
  unqualified:
    (v) =>
    <a>(env: Map<a>): ((arg1: a) => Map<a>) =>
    (value: a) => ({
      local: {...env.local, [identifierToString(v)]: value},
      plugins: env.plugins,
    }),
  qualified:
    (v) =>
    <a>(env: Map<a>): ((arg1: a) => Map<a>) =>
    (value: a) => ({
      local: env.local,
      plugins: env.plugins.set(v, value),
    }),
});

export function insert<A>(
  env: Map<A>,
  variable: Variable.VariableType,
  value: A
): Map<A> {
  if (get<A>(env, variable) === value) return env;

  return _insert(variable)(env)(value);
}

const _get = Variable.match({
  unqualified:
    (v) =>
    <a>(env: Map<a>): undefined | a => {
      /**
       * We check the property is on the env and not up the prototype chain
       * to avoid attackers from using the prototype chain to execute malicious code.
       */
      if (!Object.hasOwn(env.local, v)) {
        return undefined;
      }

      return env.local[v];
    },
  qualified:
    (v) =>
    <a>(env: Map<a>): undefined | a =>
      env.plugins.get(v),
});

export function get<A>(
  env: Map<A>,
  variable: Variable.VariableType
): undefined | A {
  return _get(variable)(env);
}

export function update<A>(
  env: Map<A>,
  variable: Variable.VariableType,
  updater: (arg1: A) => A
): Map<A> {
  const currentValue = get<A>(env, variable);
  if (currentValue === undefined) return env;

  const value = updater(currentValue);

  if (currentValue === value) return env;

  return insert<A>(env, variable, value);
}

export function reduce<T, A>(
  env: Map<A>,
  cb: (arg1: T, arg2: A, arg3: Variable.VariableType) => T,
  init: T
): T {
  let result = init;

  for (const key in env.local) {
    const variable = Variable.unqualified(identifier(key));
    // @ts-expect-error - TS2345 - Argument of type 'A | undefined' is not assignable to parameter of type 'A'.
    result = cb(result, env.local[identifier(key)], variable);
  }

  return env.plugins.reduce(cb, result);
}

/**
 * Filters VariableMap by provided predicate
 */
export function filter<A>(
  env: Map<A>,
  cb: (arg1: Variable.VariableType, arg2: A) => boolean
): Map<A> {
  return reduce(
    env,
    (acc, value, variable) =>
      cb(variable, value) ? insert(acc, variable, value) : acc,
    empty()
  );
}

/**
 * Count the number of entries in the VariableMap
 */
export function size<A>(env: Map<A>): number {
  return Object.keys(env.local).length + env.plugins.size();
}

/**
 * Get local values
 */
export function getLocal<A>(env: Map<A>): Record<Identifier, A> {
  return env.local;
}

/**
 * Get plugin values
 */
export function getPlugins<A>(env: Map<A>): QualifiedMap<Identifier, A> {
  return env.plugins;
}

const emptyVariableMap: Map<never> = {
  local: {},
  plugins: QualifiedMap.empty(),
};
export const empty = <A = never>(): Map<A> => emptyVariableMap;
